import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@/components/Tooltip';
import { getTheme, toggleTheme, subscribeTheme, THEME_IS_LIGHT, type Theme } from '@/lib/theme';

/**
 * Tiny moon/sun button. Lives in the user box / TopBar. Renders inline
 * SVGs so we don't add an extra lucide import for two glyphs — they're
 * stroke-only and inherit `currentColor`, matching the rest of our
 * iconography.
 */
export function ThemeToggle({ className = '' }: { className?: string }) {
  const { t } = useTranslation();
  const [theme, setLocalTheme] = useState<Theme>(() => getTheme());

  useEffect(() => subscribeTheme(setLocalTheme), []);

  function flip() {
    toggleTheme();
  }

  const isDark = !THEME_IS_LIGHT[theme];
  return (
    <Tooltip label={isDark ? t('theme.light') : t('theme.dark')}>
    <button
      type="button"
      onClick={flip}
      aria-label={isDark ? t('theme.switchToLight') : t('theme.switchToDark')}
      className={
        'group shrink-0 rounded-lg p-1.5 text-ink-dim transition-colors hover:bg-default/60 hover:text-ink ' +
        className
      }
    >
      {isDark ? (
        // moon
        <svg
          viewBox="0 0 24 24"
          width="18"
          height="18"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className="transition-transform duration-500 ease-out group-hover:-rotate-[25deg]"
        >
          <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
        </svg>
      ) : (
        // sun
        <svg
          viewBox="0 0 24 24"
          width="18"
          height="18"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className="transition-transform duration-500 ease-out group-hover:rotate-[360deg]"
        >
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
        </svg>
      )}
    </button>
    </Tooltip>
  );
}
